import { FirearmPurposeDTO } from '../services/apiClient';
import { FirearmPurposeConstants } from '../constants/FirearmPurposeConstants';
import { isdedicatedHunter, isdedicatedSportperson } from './complianceUtils';

/**
 * Filters firearm purposes based on member's FCA category.
 * 
 * Rules:
 * - Dedicated hunters can see both dedicated and occasional hunting purposes
 * - Occasional hunters can only see occasional hunting purposes (not dedicated)
 * - Dedicated sport shooters can see both dedicated and occasional sport purposes
 * - Occasional sport shooters can only see occasional sport purposes (not dedicated)
 * 
 * @param purposes - Array of firearm purposes to filter (typically linked to a firearm specific)
 * @param memberCategoryId - The member's FCA category ID
 * @returns Filtered array of firearm purposes
 */
export const filterPurposesByMemberCategory = (
    purposes: FirearmPurposeDTO[],
    memberCategoryId: string | null | undefined
): FirearmPurposeDTO[] => {
    // If no member category, return all purposes (fallback for safety)
    if (!memberCategoryId) {
        return purposes;
    }

    const isDedicatedHunter = isdedicatedHunter(memberCategoryId);
    const isDedicatedSport = isdedicatedSportperson(memberCategoryId);

    // Define which purposes are "dedicated" and need to be filtered out for occasional members
    const dedicatedHuntingPurposes = [
        FirearmPurposeConstants.DEDICATED_HUNTING,
        FirearmPurposeConstants.HUNTING_HANDGUN_DEDICATED_HUNTING,
        FirearmPurposeConstants.CARRY_HANDGUN_DEDICATED_HUNTING,
        FirearmPurposeConstants.SEMI_AUTO_RIFLE_DEDICATED_HUNTING_ON_GAME_FARM,
    ];

    const dedicatedSportPurposes = [
        FirearmPurposeConstants.DEDICATED_SPORT_SHOOTING,
    ];

    return purposes.filter((purpose) => {
        const purposeId = purpose.id?.toLowerCase();
        if (!purposeId) return true; // Keep if no ID (safety)

        // Check if this is a dedicated hunting purpose
        const isDedicatedHuntingPurpose = dedicatedHuntingPurposes.some(
            (id) => id.toLowerCase() === purposeId
        );

        // Check if this is a dedicated sport purpose
        const isDedicatedSportPurpose = dedicatedSportPurposes.some(
            (id) => id.toLowerCase() === purposeId
        );

        // Filter out dedicated hunting purposes if member is not a dedicated hunter
        if (isDedicatedHuntingPurpose && !isDedicatedHunter) {
            return false;
        }

        // Filter out dedicated sport purposes if member is not a dedicated sport shooter
        if (isDedicatedSportPurpose && !isDedicatedSport) {
            return false;
        }

        // Keep all other purposes
        return true;
    });
};
